import { useMemo } from "react";
import { useSchedulerStore } from "../state/scheduler.store";
import { LegalType } from "../types";

/**
 * Single source of truth for the candidate T&C list.
 *
 * The rule is: an internal user never accepts the candidate T&C, an external
 * (candidate) user always does. Whether the candidate is booking or
 * rescheduling makes no difference.
 *
 * An empty array does double duty — it hides the consent UI (BookingForm /
 * SubmitAvailabilityDialog derive `enableTnCState = legal.length > 0`) AND it
 * short-circuits the acceptance audit (recordTermsAcceptanceIfApplicable
 * returns early on `legal.length === 0`). Because those two behaviours are
 * coupled to one value, the internal check must live in exactly one place:
 * previously each render path re-derived it and they drifted apart, which is
 * how BookingFormWrapper ended up showing terms to internal users (R495-405).
 */
export const useLegalTerms = (): LegalType[] => {
  const verifiedInternalClient = useSchedulerStore(
    (state) => state.verifiedInternalClient
  );
  const clientId = useSchedulerStore((state) => state.clientId);
  const termsAndConditions = useSchedulerStore(
    (state) => state.meetingLink?.roosterLinksMeta?.termsAndConditions
  );

  return useMemo(() => {
    // The internalRef token is minted per-tenant, so it only counts as
    // "internal" for the tenant that minted it — otherwise a token from any
    // authenticated user of any tenant would suppress this tenant's terms.
    // Until clientId resolves we treat the visitor as a candidate, which errs
    // toward showing the terms rather than silently skipping the audit.
    const isInternal =
      !!verifiedInternalClient &&
      !!clientId &&
      verifiedInternalClient === clientId;
    return isInternal ? [] : (termsAndConditions ?? []);
  }, [verifiedInternalClient, clientId, termsAndConditions]);
};
